import { queryClient } from "@/api/utils";
import { NewGivenwellIcon } from "@/components/GivenwellLogo";
import { Card } from "@/components/ui/Card";
import { ButtonLink } from "@/components/ui/Link";
import { envCommand } from "@/modules/command-palette/env";
import { seo } from "@/utils/seo";
import { addCommands } from "@givenwell/command-palette";
import { Center, Heading, Text } from "@givenwell/components";
import { ExpandWindowIcon } from "@givenwell/core-solid-icons";
import { colors } from "@givenwell/theme";
import { QueryClientProvider } from "@tanstack/react-query";
import { createRootRouteWithContext, HeadContent, Outlet, Scripts } from "@tanstack/react-router";
import { lazy, ReactNode, Suspense, useEffect } from "react";

type RouterContext = {
  breadcrumb?: ReactNode;
  sidebar?: ReactNode;
};

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NoMatch,
  head: () => ({ meta: seo({ title: "" }) }),
});

function RootLayout() {
  useEffect(() => {
    if (import.meta.env.DEV) {
      return addCommands([envCommand]);
    }
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <HeadContent />
      <Outlet />
      <Suspense fallback={null}>
        <Toaster />
      </Suspense>
      {import.meta.env.DEV && (
        <Suspense fallback={null}>
          <Devtools />
        </Suspense>
      )}
      <Scripts />
    </QueryClientProvider>
  );
}

const Toaster = lazy(async () => {
  const { Toaster } = await import("@givenwell/components");
  return { default: Toaster };
});

const Devtools = lazy(() => import("./devtools"));

function NoMatch() {
  return (
    <Center css={{ h: "100%" }}>
      <Card>
        <NewGivenwellIcon height="50" color={colors.brand500} />

        <Heading as="h2" css={{ fontScale: "4xl", mt: 16, mb: 8 }}>
          Not found
        </Heading>

        <Text css={{ fontScale: "lg", mb: 24 }}>Sorry, there is nothing at this page.</Text>

        <ButtonLink
          css={{ w: "100%" }}
          size="lg"
          variant="filled"
          color="primary"
          to="/"
          rightIcon={<ExpandWindowIcon />}
        >
          Go back home
        </ButtonLink>
      </Card>
    </Center>
  );
}
